// frontend/src/components/tasks/TaskTimer.jsx
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import api from '../../api/api';

const TaskTimer = ({ taskId, onTimerUpdate, refreshKey = 0 }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  // ---- Utils localStorage ----
  const persistState = (running, startTime, elapsedMs) => {
    localStorage.setItem(
      `task_timer_${taskId}`,
      JSON.stringify({
        isRunning: running,
        startTime: startTime ? startTime.toISOString() : null,
        elapsedTime: elapsedMs,
      })
    );
  };

  const startLocalTimer = (startTime) => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      const now = new Date();
      const elapsed = Math.max(0, now - startTime);
      setElapsedTime(elapsed);
    }, 1000);
  };

  const clearLocalTimer = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  };

  // ---- NOUVEAU : R�cup�rer l'�tat depuis le backend ----
  const fetchTimerStatus = async () => {
    try {
      // Ajustez cette route selon votre API
      const response = await api.get(`/tasks/${taskId}/timer/status`);
      const { isRunning: backendRunning, startTime, totalTime } = response.data;

      if (backendRunning && startTime) {
        // Timer actif c�t� backend
        const start = new Date(startTime);
        const now = new Date();
        const elapsed = Math.max(0, now - start);
        
        setIsRunning(true);
        setElapsedTime(elapsed);
        startLocalTimer(start);
        persistState(true, start, elapsed);
      } else {
        // Pas de timer actif
        setIsRunning(false);
        setElapsedTime(totalTime || 0);
        clearLocalTimer();
        persistState(false, null, totalTime || 0);
      }
    } catch (err) {
      console.error('Erreur r�cup�ration statut timer:', err);
      // Fallback sur localStorage
      loadFromLocalStorage();
    }
  };

  const loadFromLocalStorage = () => {
    const stored = localStorage.getItem(`task_timer_${taskId}`);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.isRunning && parsed.startTime) {
          const startTime = new Date(parsed.startTime);
          const now = new Date();
          const elapsed = Math.max(0, now - startTime);
          setIsRunning(true);
          setElapsedTime(elapsed);
          startLocalTimer(startTime);
        } else {
          setIsRunning(false);
          setElapsedTime(parsed.elapsedTime || 0);
        }
      } catch {
        setIsRunning(false);
        setElapsedTime(0);
      }
    }
  };

  // ---- Initialisation : priorit� au backend ----
  useEffect(() => {
    fetchTimerStatus();

    return () => {
      clearLocalTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, refreshKey]);

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleStart = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      await api.post(`/tasks/${taskId}/timer`, { action: 'start' });
      const startTime = new Date();
      setIsRunning(true);
      startLocalTimer(startTime);
      persistState(true, startTime, 0);
      if (onTimerUpdate) onTimerUpdate();
    } catch (err) {
      console.error('Erreur d�marrage timer:', err);
      setError(err.response?.data?.error || 'Erreur d�marrage timer');
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    if (loading || !isRunning) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.post(`/tasks/${taskId}/timer`, { action: 'stop' });
      
      clearLocalTimer();
      setIsRunning(false);
      
      // R�cup�rer le temps total depuis la r�ponse
      const totalTime = response.data?.totalTime || elapsedTime;
      setElapsedTime(totalTime);
      persistState(false, null, totalTime);
      
      if (onTimerUpdate) onTimerUpdate();
    } catch (err) {
      console.error('Erreur arr�t timer:', err);
      const message = err.response?.data?.error || 'Erreur arr�t timer';
      setError(message);

      if (message.includes('Aucun timer en cours')) {
        clearLocalTimer();
        setIsRunning(false);
        persistState(false, null, 0);
        setElapsedTime(0);
        if (onTimerUpdate) onTimerUpdate();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={isRunning ? handleStop : handleStart}
          disabled={loading}
          className={`px-3 py-1 rounded text-xs font-medium ${
            isRunning
              ? 'bg-red-100 text-red-700 hover:bg-red-200'
              : 'bg-green-100 text-green-700 hover:bg-green-200'
          }`}
        >
          {isRunning ? 'Arr�ter' : 'D�marrer'}
        </button>
        <span className="text-xs font-mono text-gray-700">
          {formatTime(elapsedTime)}
        </span>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
};

TaskTimer.propTypes = {
  taskId: PropTypes.string.isRequired,
  onTimerUpdate: PropTypes.func,
  refreshKey: PropTypes.number,
};

export default TaskTimer;